import {useMemo, useState} from 'react'
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import * as Haptics from 'expo-haptics'
import {Feather} from '@expo/vector-icons'

import {type BlankFont, blankInputWidth} from '#/lib/blankInputWidth'
import {FeedbackBar} from '#/components/capsules/play/FeedbackBar'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {
  type FBState,
  type SlidePlayProps,
} from '#/components/capsules/play/types'
import {useTextWidths} from '#/components/capsules/play/useTextWidths'
import {
  useFlashBookPlayPalette,
  useInkStroke,
  usePlayContentPadding,
  usePlayOptionRowRhythm,
  usePlayPrimaryAction,
  usePlayStackGap,
  usePlaySuppressOwnSurface,
  usePlayTextStyle,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {useFlashBookInkSound} from '#/components/flashbooks/useFlashBookInkSound'
import {SLIDE_TYPE_META} from '#/types/capsule'

/**
 * What an unanswered blank offers, in the creator's language.
 *
 * A named constant because the blank is sized from it: the hint is one of the
 * things the blank may have to hold, so leaving it out of the measure would
 * make the widest state of the blank the one nothing is reserved for.
 */
const BLANK_HINT = 'choose…'

/**
 * What a blank costs beyond its writing: the pill's horizontal padding, its
 * chevron and the gap before it.
 *
 * A constant because the blank is sized from it — and because the measurement it
 * is added to is the *text* room, which is what the words actually need.
 */
const BLANK_CHROME = 36

/**
 * The blank's own type, in one place: the heaviest weight it is ever shown in.
 *
 * The pill sets an answered gap a weight heavier than its hint, and one reserved
 * width has to hold both, so the reserve is measured in the heavier of the two.
 */
const BLANK_TYPE = {fontSize: 15, fontWeight: '700'} as const

/**
 * The gap's own numbers, before the page's reading type is applied over them.
 *
 * The label and the probe are built from this one object, so the type the words
 * are *measured* in cannot drift from the type they are *printed* in.
 */
const BLANK_BASE = {fontSize: BLANK_TYPE.fontSize, lineHeight: 22} as const

export function ComplexFillBlankSlidePlay({
  slide,
  onAnswer,
  primaryColor,
  retryKey,
}: SlidePlayProps) {
  const palette = useFlashBookPlayPalette()
  const padding = usePlayContentPadding(20)
  // The sentence is printed on the page, so it is set in the book's reading
  // type and spaced in whole rules.
  const bodyText = usePlayTextStyle('body')
  const stackGap = usePlayStackGap(14)
  // Inside a book the block owns the surface: on ruled paper each item is not a
  // card but an exercise written on the lines.
  const onPaper = usePlaySuppressOwnSurface()
  const rowRhythm = usePlayOptionRowRhythm()
  const ink = useInkStroke()
  const inkSound = useFlashBookInkSound()
  // The check button is furniture on the page: a whole number of rules tall.
  // The action, in the page's own language: on paper it is ink, not a button.
  const action = usePlayPrimaryAction(49)
  const blanks = slide.complexBlanks ?? []

  /*
   * The type the gap's words are printed in — composed the way the label composes
   * it, because the width the gap reserves is measured in it.
   *
   * The label merges the page's reading type *after* the gap's own numbers (see
   * the `Text` below), so it is the page that decides the size the words are
   * actually printed at: a book sets its body type, and the gap follows it. Built
   * by hand from `BLANK_TYPE` alone, this font measured a size no label ever
   * reaches — the reserve came out narrower than the longest word it has to hold,
   * and the word wrapped the moment it was chosen.
   *
   * The weight is the heaviest the gap is ever printed in — the pill sets its
   * answer a weight heavier than its hint — so the reserve covers both states.
   * The two are spelled once, here, and used by the label and the probe together,
   * so they cannot drift apart again.
   */
  const blankFont = useMemo(
    () =>
      /*
       * `BlankFont` rather than `TextStyle`, because that is what the measure
       * needs: a size that is known, not optional. It always is — the gap's own
       * numbers set one, and the page's type only ever replaces it with another.
       */
      StyleSheet.flatten([
        {...BLANK_BASE, fontWeight: BLANK_TYPE.fontWeight},
        bodyText,
      ]) as BlankFont,
    [bodyText],
  )
  const {widths, probe} = useTextWidths(blankFont, [
    BLANK_HINT,
    ...blanks.flatMap(blank => [
      blank.answer,
      ...blank.options,
      ...(blank.alternatives ?? []),
    ]),
  ])

  // Shuffle answer + wrong options once per mount; stable across retries
  const [allOptions] = useState(() =>
    blanks.map(b => [b.answer, ...b.options].sort(() => Math.random() - 0.5)),
  )

  const [selected, setSelected] = useState<(string | null)[]>(
    Array(blanks.length).fill(null),
  )
  const [openPicker, setOpenPicker] = useState<number | null>(null)
  const [checked, setChecked] = useState(false)
  const [retried, setRetried] = useState(false)
  const [showSol, setShowSol] = useState(false)
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)

  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setSelected(Array(blanks.length).fill(null))
    setOpenPicker(null)
    setChecked(false)
    setRetried(false)
    setShowSol(false)
  }

  const checkA = (blank: (typeof blanks)[0], val: string | null) => {
    if (!val) return false
    const u = val.toLowerCase().trim()
    return (
      u === blank.answer.toLowerCase().trim() ||
      (blank.alternatives ?? []).some(a => a.toLowerCase().trim() === u)
    )
  }

  const results = blanks.map((b, i) => checkA(b, selected[i]))
  const okCount = results.filter(Boolean).length
  const maxPts = slide.points ?? blanks.length
  const pts = blanks.length ? Math.round((okCount / blanks.length) * maxPts) : 0
  const allFilled = selected.every(sv => sv !== null)

  const fbState: FBState = showSol
    ? 'solution'
    : okCount === blanks.length
      ? 'correct'
      : okCount > 0
        ? 'partial'
        : 'wrong'

  const verify = () => {
    onAnswer({
      isCorrect: okCount === blanks.length,
      points: pts,
      maxPoints: maxPts,
      solutionText: blanks.map(b => b.answer).join(' · '),
      explanation: slide.explanation,
      userAnswer: selected,
    })
    setChecked(true)
  }

  const retry = () => {
    setSelected(Array(blanks.length).fill(null))
    setOpenPicker(null)
    setChecked(false)
    setRetried(true)
  }

  return (
    <PlayScroller
      style={{flex: 1}}
      contentContainerStyle={{padding, gap: stackGap, paddingBottom: 40}}
      keyboardShouldPersistTaps="handled">
      {/* The probe the blanks are measured with; see `useTextWidths`. */}
      {probe}
      {!!slide.question && (
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>
      )}

      <View style={{gap: stackGap}}>
        {blanks.map((blank, i) => {
          const sv = selected[i]
          const isOk = checked && results[i]
          const isBad = checked && !results[i]
          const isOpen = openPicker === i && !checked

          const parts = blank.sentence.split('___')
          const before = parts[0] ?? ''
          const after = parts.slice(1).join('___')
          const beforeWords = before.trim().split(/\s+/).filter(Boolean)
          const afterWords = after.trim().split(/\s+/).filter(Boolean)

          /*
           * The blank is the size of what goes in it, from the start.
           *
           * It used to be the width of its own hint, `choose…`, and then jumped
           * to the width of the chosen answer — so answering re-wrapped the
           * printed sentence under the reader's finger, and the words on either
           * side of a blank moved for a reason the reader had nothing to do
           * with. Reserving the width of the longest thing the blank can ever
           * hold — the hint, the answer, its alternates, and the distractors
           * offered beside it — keeps the sentence in the shape it had while it
           * was being read. The hint is in that list because an unanswered blank
           * is a state it is *shown* in, and a short answer is the case where
           * leaving it out makes the blank shrink rather than grow.
           *
           * The padding is the pill's own horizontal padding, its chevron and
           * the gap before it, so the floor is the *text* room rather than the
           * box.
           */
          const blankWidth = blankInputWidth(
            [
              BLANK_HINT,
              blank.answer,
              ...blank.options,
              ...(blank.alternatives ?? []),
            ],
            {
              font: blankFont,
              padding: BLANK_CHROME,
              measured: widths,
              min: 64,
            },
          )

          return (
            <View
              key={blank.id}
              style={[
                {
                  borderRadius: 14,
                  borderWidth: 1.5,
                  padding: 14,
                  gap: 10,
                  borderColor: isOk
                    ? palette.positive
                    : isBad
                      ? palette.negative
                      : palette.border,
                  backgroundColor: isOk
                    ? palette.positive + '0a'
                    : isBad
                      ? palette.negative + '0a'
                      : palette.surface,
                },
                // On paper the item is written straight onto the lines: no
                // card, and the judgement is carried by the ink of the answer
                // rather than by a tinted panel.
                onPaper && rowRhythm,
                onPaper && {
                  backgroundColor: 'transparent',
                  paddingLeft: 0,
                  paddingRight: 0,
                  gap: 0,
                },
              ]}>
              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 4,
                  // On paper the wrapped lines are held together by the ruling
                  // itself; any extra row gap pushes the next line off it.
                  rowGap: onPaper ? 0 : 6,
                }}>
                {beforeWords.map((w, wi) => (
                  <Text
                    key={`b${wi}`}
                    style={[
                      {color: palette.body, fontSize: 15, lineHeight: 22},
                      bodyText,
                    ]}>
                    {w}
                  </Text>
                ))}

                {/* Selector pill */}
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={() => {
                    if (!checked) setOpenPicker(isOpen ? null : i)
                  }}
                  style={[
                    {
                      paddingHorizontal: 10,
                      paddingVertical: 4,
                      borderRadius: 8,
                      borderWidth: 1.5,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4,
                      minWidth: blankWidth,
                    },
                    // On paper the selector is a blank to write in, not a
                    // button to press: one line tall, ruled underneath.
                    onPaper && {
                      borderRadius: 0,
                      borderWidth: 0,
                      borderBottomWidth: 2,
                      paddingVertical: 0,
                      minHeight: bodyText.lineHeight,
                    },
                    sv
                      ? {
                          borderColor: isOk
                            ? palette.positive
                            : isBad
                              ? palette.negative
                              : primaryColor + '88',
                          backgroundColor: isOk
                            ? palette.positive + '18'
                            : isBad
                              ? palette.negative + '18'
                              : primaryColor + '22',
                        }
                      : {
                          borderStyle: 'dashed',
                          borderColor: isOpen ? primaryColor : palette.muted,
                          backgroundColor: isOpen
                            ? primaryColor + '15'
                            : palette.surface,
                        },
                  ]}>
                  <Text
                    style={[
                      {
                        ...BLANK_BASE,
                        fontWeight: sv ? BLANK_TYPE.fontWeight : '500',
                        color: sv
                          ? isOk
                            ? palette.positive
                            : isBad
                              ? palette.negative
                              : primaryColor
                          : isOpen
                            ? primaryColor
                            : palette.muted,
                      },
                      bodyText,
                    ]}>
                    {sv ?? BLANK_HINT}
                  </Text>
                  {sv && checked ? (
                    <Feather
                      name={isOk ? 'check-circle' : 'x-circle'}
                      size={13}
                      color={isOk ? palette.positive : palette.negative}
                    />
                  ) : (
                    <Feather
                      name={isOpen ? 'chevron-up' : 'chevron-down'}
                      size={12}
                      color={
                        sv
                          ? primaryColor + '88'
                          : isOpen
                            ? primaryColor
                            : palette.muted
                      }
                    />
                  )}
                </TouchableOpacity>

                {afterWords.map((w, wi) => (
                  <Text
                    key={`a${wi}`}
                    style={[
                      {color: palette.body, fontSize: 15, lineHeight: 22},
                      bodyText,
                    ]}>
                    {w}
                  </Text>
                ))}
              </View>

              {/* Options revealed as chips below the sentence */}
              {isOpen && (
                <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 7}}>
                  {(allOptions[i] ?? []).map((opt, oi) => (
                    <TouchableOpacity
                      accessibilityRole="button"
                      key={oi}
                      onPress={() => {
                        void Haptics.impactAsync(
                          Haptics.ImpactFeedbackStyle.Light,
                        )
                        // Choosing a word for a gap is a marking gesture.
                        inkSound.mark()
                        setSelected(sel => {
                          const n = [...sel]
                          n[i] = opt
                          return n
                        })
                        setOpenPicker(null)
                      }}
                      style={{
                        paddingHorizontal: 14,
                        paddingVertical: 8,
                        borderRadius: 10,
                        borderWidth: 1,
                        backgroundColor: palette.surfaceAlt,
                        borderColor: palette.border,
                      }}>
                      <Text style={{color: palette.body, fontSize: 14}}>
                        <Text style={ink(primaryColor)}>{opt}</Text>
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              {/* Solution reveal */}
              {showSol && (
                <Text
                  style={{
                    color: palette.positive,
                    fontSize: 12,
                    fontWeight: '600',
                  }}>
                  ✓ {blank.answer}
                </Text>
              )}
            </View>
          )
        })}
      </View>

      {/* Check button — hidden until every blank has a selection */}
      {!checked && !showSol && allFilled && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={verify}
          style={[
            {
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              paddingVertical: 14,
              borderRadius: 14,
              backgroundColor: primaryColor,
            },
            action.style,
          ]}>
          <Feather
            name="check"
            size={16}
            color={action.ink(true) ?? palette.onAccent}
          />
          <Text
            style={[
              {
                color: action.ink(true) ?? palette.onAccent,
                fontSize: 16,
                fontWeight: '700',
              },
              action.labelStyle(true),
            ]}>
            Check
          </Text>
        </TouchableOpacity>
      )}

      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={!showSol ? slide.explanation : undefined}
          solutionText={blanks.map(b => b.answer).join(' · ')}
          onRetry={!retried ? retry : undefined}
          onShowSolution={
            !showSol
              ? () => {
                  setShowSol(true)
                  setChecked(true)
                }
              : undefined
          }
        />
      )}
    </PlayScroller>
  )
}
